import React, { useEffect, useState, useCallback, useRef } from "react";
import { post } from "../../../helper/Api_helper";
import {
  VYLABS_BASE_URL,
  CANDIDATES_BY_VENDOR,
} from "../../../helper/url_helper";
import Card from "../../../Common/Card/Card";
import DynamicSkeleton from "../../../Common/Skeleton/Skeleton";
import AnimationPlayer from "../../../Common/Animation-Player/AnimationPlayer";
import sendIconDarkAnimation from "../../../Assets/Json/nodata-found-robot-animation.json";
import { getStageColor } from "../../../Common/constants/stageColors";
import "../Candidates/Candidates.css";
import "./Vendors.css";

function VendorsCandidateList(props) {
  const { selectedVendor, getVendorID, onCandidateCardView } = props;
  const [candidates, setCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasCandidates, setHasCandidates] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();

  const fetchCandidatesByVendor = async (customOffset = offset) => {
    try {
      if (customOffset === 0) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      const vendorId = getVendorID || selectedVendor?.id;
      if (!vendorId) {
        console.warn("No vendor ID available");
        setIsLoading(false);
        setIsLoadingMore(false);
        return;
      }
      const response = await post(
        VYLABS_BASE_URL,
        `${CANDIDATES_BY_VENDOR}?limit=30&offset=${customOffset}`,
        { vendorID: vendorId },
      );
      const candidatesData = response?.candidates || [];
      const transformedCandidates = candidatesData.map((candidate) => ({
        ...candidate,
        parsedResumeData: candidate.parsed_resume
          ? JSON.parse(candidate.parsed_resume)
          : null,
        stage: candidate.status,
      }));

      setCandidates((prev) => {
        if (customOffset === 0) return transformedCandidates;
        return [...prev, ...transformedCandidates];
      });
      setHasCandidates(
        customOffset === 0 ? transformedCandidates.length > 0 : true,
      );
      setHasMore(candidatesData.length === 30);
    } catch (error) {
      console.error("Error fetching candidates by vendor:", error);
      if (customOffset === 0) {
        setCandidates([]);
        setHasCandidates(false);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    setCandidates([]);
    setOffset(0);
    setHasMore(true);
    fetchCandidatesByVendor(0);
  }, [selectedVendor, getVendorID]);

  useEffect(() => {
    if (offset > 0) {
      fetchCandidatesByVendor(offset);
    }
  }, [offset]);

  const lastElement = useCallback(
    (node) => {
      if (isLoading || isLoadingMore || !hasMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setOffset((prev) => prev + 30);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, isLoadingMore, hasMore],
  );

  const setTagColor = (tag) => {
    return getStageColor(tag);
  };

  const handleCardView = (candidate, e) => {
    e.stopPropagation();
    if (onCandidateCardView) {
      onCandidateCardView(candidate);
    }
  };

  if (isLoading) {
    return (
      <div className="vendors-candidates-container">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <div className="overall-card-container">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="candidate-card-outer-container">
                <DynamicSkeleton
                  variant="rectangular"
                  dimensions={{
                    width: "100%",
                    height: "200px",
                    sx: { borderRadius: "16px" },
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!hasCandidates) {
    return (
      <div className="no-candidates-container">
        <div className="vendor-workspace-outer-container">
          <h3>This vendor doesn't have any candidates assigned yet.</h3>
          <AnimationPlayer
            animationSrc={sendIconDarkAnimation}
            playerHeight="300px"
            playerWidth="300px"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="vendors-candidates-container">
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 0,
          gap: "48px",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            height: "26px",
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: "20px",
            lineHeight: "26px",
            letterSpacing: "-0.01em",
            color: "var(--header__color)",
          }}
        >
          Vendor's Candidates ({candidates.length})
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <div className="overall-card-container">
          {candidates.map((candidate, index) => (
            <div
              key={candidate.id || index}
              className="candidate-card-outer-container"
              ref={index === candidates.length - 1 ? lastElement : null}
            >
              <Card
                name={candidate.name}
                email={candidate.email}
                phone={candidate.phone}
                location={candidate.city}
                role={
                  candidate.designation && candidate.designation.trim() !== ""
                    ? candidate.designation
                    : candidate.parsedResumeData?.CareerHistory?.JobDetails?.[0]
                          ?.JobTitle
                      ? candidate.parsedResumeData.CareerHistory.JobDetails[0]
                          .JobTitle
                      : "Not specified"
                }
                tag={candidate.stage}
                tagBackgroundColor={setTagColor(candidate.stage)}
                experience={
                  candidate.parsedResumeData?.BasicInfo?.YearsOfExperience !==
                  undefined
                    ? candidate.parsedResumeData?.BasicInfo?.YearsOfExperience
                    : candidate.experience
                }
                skills={
                  candidate.parsedResumeData?.Skills || candidate.skills || []
                }
                cardbackgroundColor="var(--body__background)"
                cardColor="black"
                skillbackgroundColor="#E0DEFF"
                skillColor="#2D23DB"
                viewEvent={(e) => handleCardView(candidate, e)}
                applications={candidate.applications}
              />
            </div>
          ))}
        </div>
        {isLoadingMore && (
          <div className="overall-card-container">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={`loading-more-${index}`}
                className="candidate-card-outer-container"
              >
                <DynamicSkeleton
                  variant="rectangular"
                  dimensions={{
                    width: "100%",
                    height: "200px",
                    sx: { borderRadius: "16px" },
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default VendorsCandidateList;
